import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Cancel from "@mui/icons-material/CancelOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import MagicIcon from "@mui/icons-material/AutoFixHigh";
import LinkIcon from "@mui/icons-material/Link";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import { useSearchParams } from "react-router-dom";
import { 
  fetchJDList, 
  fetchJDContent, 
  createJob, 
  createApplicationForm, 
  generateFlyer 
} from "../Services/jobService";
import { improveJD, generateJobContent } from "../Services/OpenAIService";
import { getCompanyDescription, getCompanyId } from "../Services/authService";

interface JD {
  id: string;
  title: string;
}

interface FlyerContent {
  jobTitle: string;
  jobDescription: string | string[];
  specifications: string[];
  requirements: string[];
  salaryAndBenefits: string[];
}

interface GeneratedContent {
  linkedinPost: string;
  screeningQuestions: string[];
  flyerContent: FlyerContent;
  emailContent: string;
}

const PostNewJob: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Step handling
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Select JD", "Edit & Improve", "Generate Content", "Create Application"];
  
  // Original state variables
  const [jds, setJds] = useState<JD[]>([]);
  const [selectedJD, setSelectedJD] = useState<JD | null>(null);
  const [jdContent, setJdContent] = useState("");
  const [originalJdContent, setOriginalJdContent] = useState("");
  const [additionalConsideration, setAdditionalConsideration] = useState("");
  const [whatToImprove, setWhatToImprove] = useState("");
  const [location, setLocation] = useState("");
  const [deadline, setDeadline] = useState("");
  const [companyBackground, setCompanyBackground] = useState("");
  const [isImproving, setIsImproving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [editableLinkedinPost, setEditableLinkedinPost] = useState("");
  const [editableScreeningQuestions, setEditableScreeningQuestions] = useState<string[]>([]);
  const [editableFlyerContent, setEditableFlyerContent] = useState<FlyerContent>({
    jobTitle: "",
    jobDescription: [],
    specifications: [],
    requirements: [],
    salaryAndBenefits: [],
  });
  const [editableEmailContent, setEditableEmailContent] = useState("");
  const [applicationFormLink, setApplicationFormLink] = useState("");
  const [jobId, setJobId] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const [flyerImage, setFlyerImage] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const baseApplicationUrl = "https://hiresmart.digitaltxc.com/availablejobs";

  useEffect(() => {
    const loadData = async () => {
      try {
        showSnackbar("Loading job descriptions...", "info");
        const data = await fetchJDList(getCompanyId());
        setJds(data);
        setCompanyBackground(getCompanyDescription());
        if (data.length > 0) {
          showSnackbar("Job descriptions loaded successfully", "success");
        } else {
          showSnackbar("No job descriptions found", "info");
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showSnackbar("Failed to load job descriptions", "error");
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const loadJDContent = async () => {
      if (selectedJD) {
        try {
          showSnackbar(`Loading content for: ${selectedJD.title}`, "info");
          const content = await fetchJDContent(selectedJD.id);
          setJdContent(content);
          setOriginalJdContent(content);
          setEditableFlyerContent(prev => ({
            ...prev,
            jobTitle: selectedJD.title,
          }));
          showSnackbar("Job description content loaded", "success");
          
          // Auto advance to next step when JD is selected
          if (activeStep === 0) {
            setActiveStep(1);
          }
        } catch (error) {
          console.error("Error loading JD content:", error);
          showSnackbar("Failed to load job description content", "error");
        }
      }
    };
    loadJDContent();
  }, [selectedJD]);

  const showSnackbar = (message: string, severity: "success" | "error" | "info" | "warning" = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleImproveJD = async () => {
    if (!jdContent) {
      showSnackbar("Please enter a job description to improve", "warning");
      return;
    }

    setIsImproving(true);
    try {
      showSnackbar("Improving job description...", "info");
      const improvedContent = await improveJD({ jdContent, whatToImprove });
      if (improvedContent) {
        setJdContent(improvedContent);
        showSnackbar("Job description improved successfully!", "success");
      }
    } catch (error) {
      console.error("Error improving JD:", error);
      showSnackbar("Failed to improve job description", "error");
    } finally {
      setIsImproving(false);
    }
  };

  const handleGenerateJob = async () => {
    if (!jdContent) {
      showSnackbar("Please provide job description content", "warning");
      return;
    }
    if (!location) {
      showSnackbar("Please specify a location", "warning");
      return;
    }
    if (!deadline) {
      showSnackbar("Please specify a deadline", "warning");
      return;
    }

    setIsGenerating(true);
    try {
      showSnackbar("Generating job content...", "info");
      const response = await generateJobContent({
        companyBackground,
        jdContent,
        location,
        deadline
      });
      
      const parsedContent: GeneratedContent = JSON.parse(response || "{}");
      setGeneratedContent(parsedContent);
      setEditableLinkedinPost(parsedContent.linkedinPost);
      setEditableScreeningQuestions(parsedContent.screeningQuestions);
      setEditableFlyerContent(parsedContent.flyerContent);
      setEditableEmailContent(parsedContent.emailContent);
      
      showSnackbar("Job content generated successfully!", "success");
      
      // Auto advance to next step
      setActiveStep(2);
    } catch (error) {
      console.error("Error generating job:", error);
      showSnackbar("Failed to generate job content", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateApplicationForm = async () => {
    if (!editableFlyerContent.jobTitle) {
      showSnackbar("Job title is required", "warning");
      return;
    }

    setIsCreatingForm(true);
    try {
      showSnackbar("Creating job and application form...", "info");
      const jobResponse = await createJob({
        title: editableFlyerContent.jobTitle,
        description: jdContent,
        additionalConsideration,
        location,
        deadline,
        linkedinPost: editableLinkedinPost,
        screeningQuestions: editableScreeningQuestions,
        FlyerJobDescriptions: Array.isArray(editableFlyerContent.jobDescription) 
          ? editableFlyerContent.jobDescription 
          : [editableFlyerContent.jobDescription],
        FlyerJobSpecifications: Array.isArray(editableFlyerContent.specifications)
          ? editableFlyerContent.specifications
          : [editableFlyerContent.specifications],
        emailContent: editableEmailContent,
      });

      const formResponse = await createApplicationForm({
        jobId: jobResponse.id,
        jobTitle: editableFlyerContent.jobTitle,
        jobDescription: editableFlyerContent.jobDescription,
        location,
        deadline,
        screeningQuestions: editableScreeningQuestions,
      });

      const formLink = `${baseApplicationUrl}/${formResponse.id}`;
      setApplicationFormLink(formLink);
      setJobId(jobResponse.id);

      showSnackbar("Generating job flyer...", "info");
      const flyerResponse = await generateFlyer(jobResponse.id, formLink);
      if (flyerResponse?.flyerUrl) {
        setFlyerImage(flyerResponse.flyerUrl);
      }

      showSnackbar("Application form created successfully!", "success");
      
      // Auto advance to final step
      setActiveStep(3);
    } catch (error) {
      console.error("Error creating form:", error);
      showSnackbar("Failed to create application form", "error");
    } finally {
      setIsCreatingForm(false);
    }
  };

  const copyToClipboard = (text: string, itemName: string = "Content") => {
    navigator.clipboard.writeText(text)
      .then(() => showSnackbar(`${itemName} copied to clipboard!`, "success"))
      .catch(() => showSnackbar("Failed to copy to clipboard", "error"));
  };

  const handleRedo = () => {
    setGeneratedContent(null);
    setEditableLinkedinPost("");
    setEditableScreeningQuestions([]);
    setEditableFlyerContent({
      jobTitle: "",
      jobDescription: [],
      specifications: [],
      requirements: [],
      salaryAndBenefits: [],
    });
    setEditableEmailContent("");
    setJdContent("");
    setLocation("");
    setDeadline("");
    setSelectedJD(null);
    setAdditionalConsideration("");
    setApplicationFormLink("");
    setFlyerImage(null);
    setActiveStep(0);
    showSnackbar("Form reset! Start over with a new job", "info");
  };

  const handleStepChange = (step: number) => {
    // Validation before allowing step changes
    if (step > 0 && !selectedJD && !jdContent) {
      showSnackbar("Please select or enter a job description first", "warning");
      return;
    }
    
    if (step > 1 && (!location || !deadline)) {
      showSnackbar("Please complete all required fields before proceeding", "warning");
      return;
    }
    
    if (step > 2 && !generatedContent) {
      showSnackbar("Please generate job content first", "warning");
      return;
    }
    
    setActiveStep(step);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader 
              title="Select Job Description" 
              subheader="Choose an existing template or create a new job description"
            />
            <CardContent>
              <Autocomplete
                options={jds}
                getOptionLabel={(jd) => jd.title}
                value={selectedJD}
                onChange={(_, newValue) => setSelectedJD(newValue)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select Job Description" 
                    fullWidth 
                    sx={{ mb: 2 }} 
                  />
                )}
              />
              
              <Divider sx={{ my: 2 }}>
                <Chip label="OR" />
              </Divider>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter job description manually if not in the list above
              </Typography>
              
              <TextField
                label="Job Description"
                multiline
                minRows={4}
                maxRows={10}
                fullWidth
                value={jdContent}
                onChange={(e) => setJdContent(e.target.value)}
                placeholder="Enter job description details here..."
              />
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="text"
                  disabled
                >
                  Back
                </Button>
                
                <Button
                  variant="contained"
                  onClick={() => handleStepChange(1)}
                  disabled={!selectedJD && !jdContent}
                >
                  Next: Edit & Improve
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
      
      case 1:
        return (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader 
              title="Edit & Improve Job Description"
              subheader="Enhance the job description and add important details"
              action={
                <Tooltip title="Reset to original">
                  <IconButton 
                    onClick={() => setJdContent(originalJdContent)}
                    disabled={!originalJdContent || originalJdContent === jdContent}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Job Description"
                    multiline
                    minRows={6}
                    maxRows={12}
                    fullWidth
                    value={jdContent}
                    onChange={(e) => setJdContent(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="What would you like to improve?"
                    placeholder="E.g., Make it more concise, add more details about tech requirements, etc."
                    multiline
                    fullWidth
                    minRows={2}
                    value={whatToImprove}
                    onChange={(e) => setWhatToImprove(e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Additional Considerations (Internal use only)"
                    placeholder="e.g. Preferred qualifications, experience, location"
                    multiline
                    fullWidth
                    minRows={2}
                    value={additionalConsideration}
                    onChange={(e) => setAdditionalConsideration(e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Location"
                    placeholder="e.g., Remote, Lahore, Punjab, Hybrid - Palistan"
                    fullWidth
                    required
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    InputProps={{
                      startAdornment: <LocationOnIcon color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Application Deadline"
                    type="date"
                    required
                    fullWidth
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <EventIcon color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleImproveJD}
                  disabled={isImproving || !whatToImprove}
                  startIcon={<MagicIcon />}
                  sx={{ mr: 2 }}
                >
                  {isImproving ? <CircularProgress size={24} /> : "Improve Description"}
                </Button>
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => handleStepChange(0)}
                >
                  Back: Select JD
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleGenerateJob}
                  disabled={isGenerating || !jdContent || !location || !deadline}
                >
                  {isGenerating ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Generate Job Content"
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
      
      case 2:
        return (
          <>
            {generatedContent && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardHeader 
                  title="Review Generated Content"
                  subheader="Edit any part of the generated content before creating the application form"
                />
                <CardContent>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">
                        <strong>LinkedIn Post</strong>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <TextField
                          multiline
                          minRows={3}
                          maxRows={10}
                          fullWidth
                          value={editableLinkedinPost}
                          onChange={(e) => setEditableLinkedinPost(e.target.value)}
                          sx={{ mr: 1 }}
                        />
                        <Tooltip title="Copy to clipboard">
                          <IconButton onClick={() => copyToClipboard(editableLinkedinPost, "LinkedIn post")}>
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">
                        <strong>Screening Questions</strong> ({editableScreeningQuestions.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {editableScreeningQuestions.map((question, index) => (
                        <TextField
                          key={index}
                          label={`Question ${index + 1}`}
                          fullWidth
                          value={question}
                          onChange={(e) => {
                            const newQuestions = [...editableScreeningQuestions];
                            newQuestions[index] = e.target.value;
                            setEditableScreeningQuestions(newQuestions);
                          }}
                          sx={{ mb: 2 }}
                        />
                      ))}
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => setEditableScreeningQuestions([...editableScreeningQuestions, ""])}
                        sx={{ mt: 1 }}
                      >
                        + Add Question
                      </Button>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">
                        <strong>Flyer Content</strong>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        label="Job Title"
                        fullWidth
                        value={editableFlyerContent.jobTitle}
                        onChange={(e) => setEditableFlyerContent({
                          ...editableFlyerContent,
                          jobTitle: e.target.value
                        })}
                        sx={{ mb: 2 }}
                      />
                      <Divider sx={{ my: 2 }} />

                      <TextField
                        label="Job Description"
                        multiline
                        minRows={3}
                        fullWidth
                        value={editableFlyerContent.jobDescription}
                        onChange={(e) => setEditableFlyerContent({
                          ...editableFlyerContent,
                          jobDescription: e.target.value
                        })}
                        sx={{ mb: 2 }}
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">
                        <strong>Email Content</strong>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        multiline
                        fullWidth
                        minRows={4}
                        value={editableEmailContent}
                        onChange={(e) => setEditableEmailContent(e.target.value)}
                      />
                    </AccordionDetails>
                  </Accordion>
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleStepChange(1)}
                    >
                      Back: Edit Description
                    </Button>
                    
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleCreateApplicationForm}
                      disabled={isCreatingForm}
                      startIcon={isCreatingForm ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    >
                      Create Application Form
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        );
      
      case 3:
        return (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader 
              title="Application Form Created"
              titleTypographyProps={{ color: 'success.main' }}
              avatar={<CheckCircleIcon color="success" fontSize="large" />}
              subheader="Your job has been posted successfully"
            />
            <CardContent>
              <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="h6" gutterBottom>
                  {editableFlyerContent.jobTitle}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip icon={<LocationOnIcon />} label={location} />
                  <Chip icon={<EventIcon />} label={`Deadline: ${deadline}`} />
                  <Chip icon={<LinkIcon />} label="Form Available" color="primary" />
                </Box>
              </Paper>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <TextField
                  label="Application Link"
                  fullWidth
                  value={applicationFormLink}
                  InputProps={{ 
                    readOnly: true,
                    startAdornment: <LinkIcon color="primary" sx={{ mr: 1 }} />
                  }}
                  sx={{ mr: 1 }}
                />
                <IconButton onClick={() => copyToClipboard(applicationFormLink, "Application link")}>
                  <ContentCopyIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={() => window.open(applicationFormLink, "_blank")}
                    startIcon={<LinkIcon />}
                  >
                    Open Form
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => copyToClipboard(applicationFormLink, "Application link")}
                    startIcon={<ContentCopyIcon />}
                  >
                    Copy Link
                  </Button>
                </Grid>
              </Grid>

              {flyerImage && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Job Flyer Preview
                  </Typography>
                  <Paper elevation={3} sx={{ p: 2, display: 'inline-block', maxWidth: '80%' }}>
                    <Box
                      component="img"
                      src={flyerImage}
                      alt="Job Flyer"
                      sx={{ 
                        maxWidth: '100%', 
                        borderRadius: 1,
                        boxShadow: 3
                      }}
                    />
                  </Paper>
                </Box>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => handleStepChange(2)}
                >
                  Back: Edit Content
                </Button>
                
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<EditIcon />}
                  onClick={handleRedo}
                >
                  Create Another Job
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
        
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Box padding={isMobile ? 2 : 3}>
      <Card sx={{ mb: 3, bgcolor: theme.palette.background.default }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Post New Job
          </Typography>

          <Stepper 
            activeStep={activeStep} 
            alternativeLabel={!isMobile}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{ mb: 4, mt: 2 }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel onClick={() => handleStepChange(index)} sx={{ cursor: 'pointer' }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {renderStepContent()}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PostNewJob;